// ─── KeyPromptsTab.jsx ────────────────────────────────────────────────────────
// "Key Prompts" dashboard tab — scored, filterable, clustered list of natural
// user questions a brand's website should answer. See
// design_handoff_key_prompts/README.md for the full spec.

import React, { useEffect, useRef, useState } from "react";
import { apiFetch } from "../lib/apiFetch";
import { useLanguage } from "./LanguageContext";

// Reddit removed (DataForSEO doesn't index Reddit content — see keyPromptsService.js
// comment); weights below are the original 6-signal weights with Reddit's 15%
// redistributed proportionally across the remaining 5, matching the backend's
// SIGNAL_WEIGHTS in server/keyPromptsService.js.
const SIGNAL_DEFS = [
  { key: "vol", labelKey: "sigVol", pct: 41, color: "#2563eb" },
  { key: "trends", labelKey: "sigTrends", pct: 23, color: "#7c3aed" },
  { key: "paa", labelKey: "sigPaa", pct: 18, color: "#0891b2" },
  { key: "auto", labelKey: "sigAuto", pct: 12, color: "#16a34a" },
  { key: "intent", labelKey: "sigIntent", pct: 6, color: "#ca8a04" },
];

const STATUS_META = {
  vorhanden: { labelKey: "statusVorhanden", bg: "#e7f7ed", fg: "#15803d" },
  kein_content: { labelKey: "statusKeinContent", bg: "#fdeaea", fg: "#b91c1c" },
  landingpage_fehlt: { labelKey: "statusLandingpageFehlt", bg: "#fdf1e2", fg: "#b45309" },
  oberflaechlich: { labelKey: "statusOberflaechlich", bg: "#fef6df", fg: "#92620a" },
};

const SCORE_THRESHOLD_HIGHLIGHT = 90;

function getTier(score) {
  if (score >= 95) return { bg: "#fdeceb", fg: "#c0392b" };
  if (score >= 90) return { bg: "#fdf1e2", fg: "#b45309" };
  if (score >= 80) return { bg: "#fef6df", fg: "#92620a" };
  return { bg: "#eef1f6", fg: "#475569" };
}

function PromptCard({ prompt, expanded, onToggle, t }) {
  const tier = getTier(prompt.score);
  const statusMeta = STATUS_META[prompt.status] || STATUS_META.kein_content;
  const isCritical = prompt.score >= SCORE_THRESHOLD_HIGHLIGHT;

  return (
    <div style={{ background: "#fff", border: `1px solid ${isCritical ? "#f3c9c9" : "#e7e9ee"}`, borderRadius: 12, overflow: "hidden" }}>
      <div onClick={onToggle} style={{ display: "flex", alignItems: "center", gap: 16, padding: "14px 18px", cursor: "pointer" }}>
        <div style={{ width: 44, height: 44, borderRadius: 10, background: tier.bg, color: tier.fg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, fontWeight: 800, flexShrink: 0 }}>
          {prompt.score}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14.5, fontWeight: 600, color: "#1a2233" }}>{prompt.q}</div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 5, flexWrap: "wrap" }}>
            <span style={{ fontSize: 11, fontWeight: 700, padding: "3px 8px", borderRadius: 6, background: statusMeta.bg, color: statusMeta.fg }}>
              {t(`keyPrompts.${statusMeta.labelKey}`)}
            </span>
            <span style={{ fontSize: 12.5, color: "#64748b" }}>→ {prompt.rec}</span>
          </div>
        </div>
        <div style={{ fontSize: 13, color: "#94a3b8", flexShrink: 0 }}>{expanded ? "▾" : "▸"}</div>
      </div>
      {expanded && (
        <div style={{ padding: "0 18px 16px 78px", borderTop: "1px solid #f2f4f7" }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.05em", color: "#94a3b8", margin: "14px 0 10px" }}>
            {t("keyPrompts.scoreBreakdown")}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 7, maxWidth: 460 }}>
            {SIGNAL_DEFS.map((sd) => {
              const value = prompt.signals?.[sd.key];
              return (
                <div key={sd.key} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ width: 150, fontSize: 12, color: "#475569" }}>{t(`keyPrompts.${sd.labelKey}`)}</div>
                  <div style={{ flex: 1, height: 6, background: "#eef1f6", borderRadius: 4, overflow: "hidden" }}>
                    <div style={{ height: "100%", width: `${value ?? 0}%`, background: sd.color }} />
                  </div>
                  <div style={{ width: 30, fontSize: 12, color: "#94a3b8", textAlign: "right" }}>{value ?? "–"}</div>
                </div>
              );
            })}
          </div>
          <div style={{ fontSize: 12.5, color: "#64748b", marginTop: 12 }}>
            <b style={{ color: "#1a2233" }}>{t("keyPrompts.empfehlung")}:</b> {prompt.rec}
          </div>
        </div>
      )}
    </div>
  );
}

export default function KeyPromptsTab({ brandId, brandUrl }) {
  const { t, lang } = useLanguage();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");
  const [topicFilter, setTopicFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [minScore, setMinScore] = useState(0);
  const [expanded, setExpanded] = useState({});

  // Guards against out-of-order responses: if the user switches brands quickly,
  // a slow fetch for a previous brand could otherwise resolve after (and
  // overwrite) the correct data for the brand currently being viewed.
  const currentBrandRef = useRef(brandId);
  currentBrandRef.current = brandId;

  const loadReport = async () => {
    if (!brandId) return;
    const requestedBrandId = brandId;
    setLoading(true);
    try {
      const res = await apiFetch(`/key-prompts?brandId=${requestedBrandId}`);
      const data = await res.json();
      if (currentBrandRef.current === requestedBrandId) setReport(data);
    } catch (err) {
      console.warn("Failed to load key prompts:", err);
    } finally {
      if (currentBrandRef.current === requestedBrandId) setLoading(false);
    }
  };

  useEffect(() => { loadReport(); }, [brandId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleGenerate = async () => {
    if (!brandId || !brandUrl) return;
    const requestedBrandId = brandId;
    const previousCreatedAt = report?.created_at;
    setGenerating(true);
    setError("");
    try {
      await apiFetch("/key-prompts/generate", {
        method: "POST",
        body: JSON.stringify({ brandId, url: brandUrl, lang }),
      });
      // Pipeline runs in the background — gathering real signals for every
      // question can take a few minutes, so poll generously before giving up.
      let attempts = 0;
      const poll = setInterval(async () => {
        attempts += 1;
        // Bail out silently if the user has since switched to a different brand —
        // this poll's result no longer belongs on screen.
        if (currentBrandRef.current !== requestedBrandId) { clearInterval(poll); return; }
        const res = await apiFetch(`/key-prompts?brandId=${requestedBrandId}`);
        const data = await res.json();
        if (currentBrandRef.current !== requestedBrandId) { clearInterval(poll); return; }
        if (data && data.created_at !== previousCreatedAt) {
          setReport(data);
          setGenerating(false);
          clearInterval(poll);
        } else if (attempts >= 40) {
          setGenerating(false);
          clearInterval(poll);
        }
      }, 8000);
    } catch (err) {
      setError(t("keyPrompts.generateError"));
      setGenerating(false);
    }
  };

  const toggleExpand = (id) => setExpanded((s) => ({ ...s, [id]: !s[id] }));

  const clusters = report?.clusters || [];
  const clusterOptions = clusters.map((c) => ({ id: c.id, name: c.name }));

  const filteredClusters = clusters
    .map((c) => ({
      ...c,
      prompts: c.prompts.filter((p) => p.score >= minScore && (statusFilter === "all" || p.status === statusFilter)),
    }))
    .filter((c) => (topicFilter === "all" || c.id === topicFilter) && c.prompts.length > 0);

  const totalCount = clusters.reduce((s, c) => s + c.prompts.length, 0);
  const shownCount = filteredClusters.reduce((s, c) => s + c.prompts.length, 0);

  return (
    <div>
      <div style={{ marginBottom: 6 }}>
        <div style={{ fontSize: 21, fontWeight: 700 }}>{t("keyPrompts.title")}</div>
        <div style={{ fontSize: 13.5, color: "#64748b", maxWidth: 640 }}>{t("keyPrompts.subtitle")}</div>
      </div>

      {/* Methodology panel */}
      <div style={{ background: "#fff", border: "1px solid #e7e9ee", borderRadius: 12, padding: "18px 20px", margin: "18px 0 20px" }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: "#1a2233", marginBottom: 4 }}>{t("keyPrompts.methodologyTitle")}</div>
        <div style={{ fontSize: 12.5, color: "#64748b", marginBottom: 14 }}>{t("keyPrompts.methodologyCaption")} {t("keyPrompts.methodologySub")}</div>
        <div style={{ display: "flex", height: 12, borderRadius: 6, overflow: "hidden", marginBottom: 10 }}>
          {SIGNAL_DEFS.map((sd) => (
            <div key={sd.key} style={{ width: `${sd.pct}%`, background: sd.color }} />
          ))}
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 16 }}>
          {SIGNAL_DEFS.map((sd) => (
            <div key={sd.key} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12.5, color: "#475569" }}>
              <div style={{ width: 9, height: 9, borderRadius: 2, background: sd.color }} />
              {t(`keyPrompts.${sd.labelKey}`)} · {sd.pct}%
            </div>
          ))}
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: 40, color: "#94a3b8", fontSize: 13.5 }}>{t("keyPrompts.loading")}</div>
      ) : !report ? (
        <div style={{ background: "#fff", border: "1px solid #e7e9ee", borderRadius: 12, padding: "32px 24px", textAlign: "center" }}>
          <div style={{ fontSize: 13.5, color: "#64748b", marginBottom: 16 }}>{t("keyPrompts.emptyState")}</div>
          <button onClick={handleGenerate} disabled={generating} style={{
            background: generating ? "#cbd2dd" : "#16a34a", color: "#fff", border: "none",
            padding: "10px 20px", borderRadius: 8, fontSize: 13.5, fontWeight: 600,
            cursor: generating ? "not-allowed" : "pointer",
          }}>
            {generating ? t("keyPrompts.generating") : t("keyPrompts.generateButton")}
          </button>
          {error && <div style={{ color: "#dc2626", fontSize: 12.5, marginTop: 10 }}>{error}</div>}
        </div>
      ) : (
        <>
          {/* Filter bar */}
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 18, flexWrap: "wrap" }}>
            <select value={topicFilter} onChange={(e) => setTopicFilter(e.target.value)} style={{ border: "1px solid #d7dbe3", background: "#fff", padding: "8px 12px", borderRadius: 8, fontSize: 13, fontWeight: 500, color: "#334155" }}>
              <option value="all">{t("keyPrompts.allTopics")}</option>
              {clusterOptions.map((opt) => <option key={opt.id} value={opt.id}>{opt.name}</option>)}
            </select>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={{ border: "1px solid #d7dbe3", background: "#fff", padding: "8px 12px", borderRadius: 8, fontSize: 13, fontWeight: 500, color: "#334155" }}>
              <option value="all">{t("keyPrompts.allStatuses")}</option>
              {Object.entries(STATUS_META).map(([key, meta]) => (
                <option key={key} value={key}>{t(`keyPrompts.${meta.labelKey}`)}</option>
              ))}
            </select>
            <div style={{ display: "flex", alignItems: "center", gap: 10, border: "1px solid #d7dbe3", background: "#fff", padding: "7px 14px", borderRadius: 8 }}>
              <span style={{ fontSize: 13, color: "#334155", fontWeight: 500 }}>{t("keyPrompts.minScore")}</span>
              <input type="range" min={0} max={100} step={1} value={minScore} onChange={(e) => setMinScore(Number(e.target.value))} style={{ width: 110 }} />
              <span style={{ fontSize: 12.5, color: "#64748b", width: 26 }}>{minScore}</span>
            </div>
            <div style={{ flex: 1 }} />
            <button onClick={handleGenerate} disabled={generating} style={{
              border: "1px solid #d7dbe3", background: generating ? "#f1f5f9" : "#fff", color: "#334155",
              padding: "8px 14px", borderRadius: 8, fontSize: 13, fontWeight: 500,
              cursor: generating ? "not-allowed" : "pointer",
            }}>
              {generating ? t("keyPrompts.generating") : t("keyPrompts.refreshButton")}
            </button>
            <div style={{ fontSize: 12.5, color: "#94a3b8" }}>
              {t("keyPrompts.shownOfTotal").replace("{shown}", shownCount).replace("{total}", totalCount)}
            </div>
          </div>

          {/* Grouped prompt list */}
          <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
            {filteredClusters.map((cluster) => (
              <div key={cluster.id}>
                <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 10 }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: "#1a2233" }}>{cluster.name}</div>
                  <div style={{ fontSize: 12, color: "#94a3b8" }}>{t("keyPrompts.nKeyPrompts").replace("{n}", cluster.prompts.length)}</div>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {cluster.prompts.map((prompt) => (
                    <PromptCard key={prompt.id} prompt={prompt} expanded={!!expanded[prompt.id]} onToggle={() => toggleExpand(prompt.id)} t={t} />
                  ))}
                </div>
              </div>
            ))}
            {shownCount === 0 && (
              <div style={{ textAlign: "center", padding: 40, color: "#94a3b8", fontSize: 13.5 }}>{t("keyPrompts.noResults")}</div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
